import { useLocation, useNavigate } from "react-router-dom";
import PageHeader from "../../../../shared/PageHeader/PageHeader";
import PageWrapper from "../../../../shared/PageWrapper/PageWrapper";
import {
  Box,
  Grid2,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Button as MatButton,
  Menu,
  MenuItem,
  Typography,
  TextField,
  TablePagination,
  TableSortLabel,
  Autocomplete,
} from "@mui/material";
import EditIcon from "../../../../assets/images/icons/edit.svg";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DownloadIcon from "@mui/icons-material/Download";
import ViewIcons from "./../../../../assets/images/icons/view.svg";
import ConfirmationDialog from "../../../../components/ConfirmationDialog/ConfirmationDialog";
import { useCallback, useEffect, useRef, useState } from "react";
import Search from "../../../../components/Search/Search";
import * as Yup from "yup";
import { SCHEDULE_STATUS, CANDIDATE_SOURCE } from "../../../../utils/static-masters";
import { getUser } from "../../../../utils/types";
import { useDispatch } from "react-redux";
import { setToast } from "../../../../store/slices/toastSlice";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import API from "../../../../api/axios";
import DataLoader from "../../../../shared/DataLoader/DataLoader";
import DataNotFound from "../../../../shared/DataNotFound/DataNotFound";
import moment from "moment";
import SlotStatus from "../../../../shared/SlotStatus/SlotStatus";
import FeedBackIcon from "./../../../../assets/images/icons/feedback.svg";
import DifferenceIcon from "@mui/icons-material/Difference";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DateRangePicker from "../../../../shared/DateRangePicker/DateRangePicker";
import RemarksDialog from "../../CandidateManagement/ApplyPosition/RemarksDialog";

const InterviewTrackerListView = ({ pathdata }: { pathdata?: any }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const persistedFilterState = location?.state?.filterState ?? null;
  const persistedSearchState = location?.state?.searchState ?? "";
  const persistedProjectCodeSearchState =
    location?.state?.projectCodeSearchState ?? "";
  const persistedPaginationState = location?.state?.paginationState ?? {};
  const persistedDateRangeState =
    location?.state?.dateRangeState ?? { startDate: null, endDate: null };
  const persistedCandidateSourceState = location?.state?.candidateSourceState ?? null;
  const persistedPositionState = location?.state?.positionState ?? null;
  const isReschuldingTracker = location.pathname.includes("rescheduling-tracker");

  const [searchValue, setSearchValue] = useState(persistedSearchState);
  const [projectCodeSearchValue, setProjectCodeSearchValue] = useState(
    persistedProjectCodeSearchState,
  );
  const [searchKey, setSearchKey] = useState<String>("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    persistedPaginationState.rowsPerPage ?? 20
  );
  const [sortOrder, setSortOrder] = useState<any>("asc");
  const [sortBy, setSortBy] = useState("");
  const [page, setPage] = useState(persistedPaginationState.page ?? 0);
  const [offset, setOffset] = useState<number>(
    persistedPaginationState.offset ?? 0
  );
  const [interviewerData, setInterviewerData] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [selectedScheduleStatus, setSelectedScheduleStatus] =
    useState<any>(persistedFilterState);
  const [startDate, setStartDate] = useState<Date | null>(
    persistedDateRangeState.startDate
      ? moment(persistedDateRangeState.startDate).toDate()
      : null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    persistedDateRangeState.endDate
      ? moment(persistedDateRangeState.endDate).toDate()
      : null
  );
  const [dateRange, setDateRange] = useState<any>(persistedDateRangeState);
  const [candidateSource, setCandidateSource] = useState<any>(persistedCandidateSourceState);
  const hasHandledInitialSearch = useRef(false);

  const loggedUserdata = getUser();
  const roleCode: string = loggedUserdata?.roles?.role_code ?? "";
  const dispatch = useDispatch();  



  const [positionMasters, setPositionMasters] = useState<any[]>([]);
  const [selectedPosition, setSelectedPosition] = useState<any>(persistedPositionState);

  const [openTARemarksDialog, setOpenTARemarksDialog] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);
  const [taRemark, setTaRemark] = useState("");
  const [taLoading, setTaLoading] = useState(false);

  const handleOpenTARemarks = async (id: string) => {
    setSelectedApplicationId(id);
    setTaLoading(true);
    setTaRemark(""); // Reset state before opening
    setOpenTARemarksDialog(true);
    try {
      const response = await API.get(`/positions/application/remark?application_id=${id}`);
      const data = response?.data?.data;
      let fetchedRemark = "";

      if (Array.isArray(data)) {
        fetchedRemark = data[0]?.ta_remark || data[0]?.remark || "";
      } else {
        fetchedRemark = data?.ta_remark || data?.remark || response?.data?.remark || "";
      }

      setTaRemark(fetchedRemark);
    } catch (error) {
      // console.error("Failed to fetch TA remarks", error);
      setTaRemark("");
    } finally {
      setTaLoading(false);
    }
  };

  const handleSaveTARemarks = async (remark: string) => {
    setTaLoading(true);
    try {
      await API.post("/positions/application/remark", {
        application_id: selectedApplicationId,
        remark: remark,
      });
      dispatch(
        setToast({
          message: "TA Remarks saved successfully",
          severity: "success",
          open: true,
        })
      );
      setOpenTARemarksDialog(false);
    } catch (error: any) {
      dispatch(
        setToast({
          message: error?.response?.data?.errors?.[0]?.message || "Failed to save TA remarks",
          severity: "error",
          open: true,
        })
      );
    } finally {
      setTaLoading(false);
    }
  };

  const handlePageChange = (event: any, newPage: any) => {
    setPage(newPage);
    setOffset(newPage * rowsPerPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (property: any) => {
    const isAsc = sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortBy(sortOrder === "asc" ? `-${property}` : property);
    setSearchKey(property);
  };

  const handleScheduleStatusFilterChange = (event: any, newValue: any) => {
    setOffset(0);
    setPage(0);
    setSelectedScheduleStatus(newValue);
    if (newValue == null && location?.state?.filterState) {
      navigate(location.pathname, { replace: true, state: null });
    }
  };

  const handleDateRangeChange = (range: {
    startDate: string | null;
    endDate: string | null;
  }) => {
    setDateRange(range);
    setOffset(0);
    setPage(0);
  };

  const handlePositionFilterChange = (event: any, newValue: any) => {
    setOffset(0);
    setPage(0);
    setSelectedPosition(newValue);
  };

  const handleCandidateSourceFilterChange = (event: any, newValue: any) => {
    setOffset(0);
    setPage(0);
    setCandidateSource(newValue?.id);
  };

  useEffect(() => {
    const getInterviewerData = async () => {
      setLoading(true);
      try {
        const response = await API.post("/positions/all/applied", {
          offset: offset,
          limit: rowsPerPage,
          sort: sortBy,
          search: searchValue || "",
          ...(!isReschuldingTracker && {
            app_display_status: selectedScheduleStatus?.id || "",
          }),
          start_date: dateRange?.startDate,
          end_date: dateRange?.endDate,
          position_id: selectedPosition?.id || "",
          project_code: projectCodeSearchValue || "",
          candidate_source: candidateSource,
          ...(isReschuldingTracker && {
            application_status: "rescheduling_hold"
          }),
        });
        setInterviewerData(response?.data?.data);
        // Some backends may return a smaller `total_count` on later pages.
        // Keep `count` from shrinking to prevent MUI TablePagination from clamping back to page 0.
        setTotalRecords(
          response?.data?.meta?.filter_count ??
          response?.data?.meta?.total_count ??
          0
        );
      } catch (error) {
        // console.log(error);
      } finally {
        setLoading(false);
      }
    };
    getInterviewerData();
  }, [
    offset,
    searchValue,
    searchKey,
    rowsPerPage,
    sortOrder,
    sortBy,
    selectedScheduleStatus,
    dateRange,
    selectedPosition,
    projectCodeSearchValue,
    candidateSource,
  ]);



  useEffect(() => {
    const fetchPositionMasters = async () => {
      try {
        const res = await API.get(
          "/items/master_positions?fields=id,name&limit=-1&offset=0&sort=name&filter[status][_in]=published"
        );
        setPositionMasters(res?.data?.data || []);
      } catch (error) {
        // console.log(error);
      }
    };
    fetchPositionMasters();
  }, []);

  useEffect(() => {
    navigate(location.pathname, {
      replace: true,
      state: {
        filterState: selectedScheduleStatus,
        paginationState: {
          page,
          rowsPerPage,
          offset,
        },
        searchState: searchValue,
        projectCodeSearchState: projectCodeSearchValue,
        dateRangeState: dateRange,
        candidateSourceState: candidateSource,
        positionState: selectedPosition,
      },
    });
  }, [
    navigate,
    location.pathname,
    selectedScheduleStatus,
    page,
    rowsPerPage,
    offset,
    searchValue,
    projectCodeSearchValue,
    dateRange,
    candidateSource,
    selectedPosition,
  ]);

  const displayedRows = Array.isArray(interviewerData) ? interviewerData : [];



  const handleProjectCodeSearch = useCallback(
    (val: string) => {
      if (val === projectCodeSearchValue) return;
      setPage(0);
      setOffset(0);
      setProjectCodeSearchValue(val);
    },
    [projectCodeSearchValue],
  );

  const handleExport = async () => {
    try {
      const payload = {
        offset: 0,
        limit: -1,
        sort: sortBy,
        search: searchValue || "",
        ...(!isReschuldingTracker && {
          app_display_status: selectedScheduleStatus?.id || "",
        }),
        start_date: dateRange?.startDate,
        end_date: dateRange?.endDate,
        position_id: selectedPosition?.id || "",
        project_code: projectCodeSearchValue || "",
        candidate_source: candidateSource,
        ...(isReschuldingTracker && {
          application_status: "rescheduling_hold",
        }),
        export_excel: true,
      };

      const response = await API.post("/positions/all/applied", payload, {
        responseType: "blob",
      });

      const formattedDate = moment().format("DD-MM-YYYY");
      const filename = isReschuldingTracker ? `ReschedulingTracker_${formattedDate}.xlsx` : `InterviewTracker_${formattedDate}.xlsx`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      dispatch(
        setToast({
          message: isReschuldingTracker ? "Rescheduling tracker exported successfully" : "Interview tracker exported successfully",
          severity: "success",
          open: true,
        })
      );
    } catch (error) {
      dispatch(
        setToast({
          message: "Export failed",
          severity: "error",
          open: true,
        })
      );
    }
  };

  return (
    <>
      <Grid2 container>
        <Grid2 size={{ xs: 12, sm: 8, md: 6 }} className="search-wrap">
          <Box display="flex" gap={2} sx={{ flexWrap: "wrap" }}>
            <Box sx={{ minWidth: { xs: "100%", sm: 260 } }}>
              <Search
                label="Search"
                value={searchValue}
                onSearch={(val) => {
                  if (!hasHandledInitialSearch.current) {
                    hasHandledInitialSearch.current = true;
                    if (location?.state?.searchState && val === "") {
                      // ignore initial empty search fired by Search component
                      return;
                    }
                  }
                  setSearchValue(val);
                }}
              />
            </Box>
            <Box sx={{ minWidth: { xs: "100%", sm: 260 } }}>
              <Search
                label="Search Project Code"
                value={projectCodeSearchValue}
                onSearch={handleProjectCodeSearch}
              />
            </Box>
          </Box>
        </Grid2>
        <Grid2
          size={{ xs: 12, sm: 4, md: 6 }}
          sx={{ marginTop: { xs: "10px", sm: "0px" } }}
        >
          <Box
            display="flex"
            gap={2}
            sx={{
              justifyContent: { xs: "flex-start", sm: "end" },
              flexWrap: "wrap",
            }}
          >
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onDateRangeChange={handleDateRangeChange}
              // endMaxDate={moment().toDate()}
              // startMaxDate={moment().toDate()}
            />

            <Autocomplete
              size="small"
              disablePortal
              options={CANDIDATE_SOURCE || []}
              getOptionLabel={(option) => option?.title || ""}
              value={
                CANDIDATE_SOURCE.find((opt: any) => opt.id === candidateSource) ||
                null
              }
              onChange={handleCandidateSourceFilterChange}
              componentsProps={{
                clearIndicator: { disableRipple: true },
                popupIndicator: { disableRipple: true },
              }}
              sx={{
                width: { xs: "100%", sm: "150px", md: "170px" },
                "& .MuiAutocomplete-inputRoot": {
                  backgroundColor: "white",
                  borderRadius: "10px",
                },
                "&:hover:": {
                  border: "transparent",
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Candidate Source"
                  variant="outlined"
                  placeholder="Candidate Source"
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                    sx: {
                      backgroundColor: "white",
                      padding: "6px 12px",
                      "&:hover": {
                        borderColor: "#fff",
                      },
                    },
                  }}
                />
              )}
            />

            {/*Position Filter*/}
            <Autocomplete
              size="small"
              disablePortal
              options={positionMasters}
              getOptionLabel={(option) => option?.name || ""}
              value={selectedPosition}
              onChange={handlePositionFilterChange}
              componentsProps={{
                clearIndicator: { disableRipple: true },
                popupIndicator: { disableRipple: true },
              }}
              sx={{
                width: { xs: "100%", sm: "200px", md: "220px" },
                "& .MuiAutocomplete-inputRoot": {
                  backgroundColor: "white",
                  borderRadius: "10px",
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Position"
                  variant="outlined"
                  placeholder="Position"
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                    sx: {
                      backgroundColor: "white",
                      padding: "6px 12px",
                      "&:hover": {
                        borderColor: "#fff",
                      },
                      // marginLeft:"-2px"
                    },
                  }}
                />
              )}
            />
            {/*Schedule Status*/}
            {!isReschuldingTracker && (
              <Autocomplete
                size="small"
                disablePortal
                options={SCHEDULE_STATUS || []}
                getOptionLabel={(option) => option?.title || ""}
                value={selectedScheduleStatus} // Set selected status
                onChange={handleScheduleStatusFilterChange} // Update state on change
                componentsProps={{
                  clearIndicator: { disableRipple: true },
                  popupIndicator: { disableRipple: true },
                }}
                sx={{
                  width: { xs: "50%", sm: "150px", md: "170px" },
                  "& .MuiAutocomplete-inputRoot": {
                    backgroundColor: "white",
                    borderRadius: "10px",
                  },
                  "&:hover:": {
                    border: "transparent",
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Schedule Status"
                    variant="outlined"
                    placeholder="Schedule Status"
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      sx: {
                        backgroundColor: "white",
                        padding: "6px 12px",
                        "&:hover": {
                          borderColor: "#fff",
                        },
                      },
                    }}
                  />
                )}
              />
            )}
            {roleCode === "administrator" && (
              <MatButton
                variant="outlined"
                color="inherit"
                size="small"
                endIcon={
                  <FileDownloadOutlinedIcon sx={{ color: "#8d8d8d" }} />
                }
                onClick={handleExport}
                sx={{
                  fontSize: "15px",
                  fontWeight: 400,
                  backgroundColor: "#fff",
                  color: "#8d8d8d",
                  border: "1px solid #E6E7E9",
                  borderRadius: "10px",
                  height: "37px",
                  width: "110px",
                }}
              >
                Export
              </MatButton>
            )}
          </Box>
        </Grid2>
      </Grid2>
      <Box
        marginTop={1}
        sx={{ overflowY: "auto", flex: "1 1 auto", display: "grid" }}
      >
        <TableContainer className="primary__table">
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                {/* Position Name — all roles */}
                <TableCell>
                  <TableSortLabel
                    active={sortBy === "position_name"}
                    direction={sortOrder}
                    onClick={() => handleSort("position_name")}
                  >
                    Position Name
                  </TableSortLabel>
                </TableCell>

                {/* Candidate Name — all roles */}
                <TableCell>
                  <TableSortLabel
                    active={sortBy === "candidate_name"}
                    direction={sortOrder}
                    onClick={() => handleSort("candidate_name")}
                  >
                    Candidate Name
                  </TableSortLabel>
                </TableCell>

                {/* Candidate Email — nhai_officials only */}
                {roleCode === "nhai_officials" && (
                  <TableCell align="left">Candidate Email</TableCell>
                )}

                {/* Project Code — nhai_officials only */}
                {roleCode === "nhai_officials" && (
                  <TableCell align="center">Project Code</TableCell>
                )}

                {/* Status — all roles */}
                <TableCell align="center">Status</TableCell>

                {/* Description — administrator only */}
                {roleCode !== "nhai_officials" && (
                  <TableCell>Description</TableCell>
                )}
                {roleCode !== "nhai_officials" && (
                  <TableCell>Remarks</TableCell>
                )}
                {/* Candidate Source — administrator only */}
                {roleCode !== "nhai_officials" && (
                  <TableCell align="center">Candidate Source</TableCell>
                )}

                <TableCell align="center">Interview Date</TableCell>

                {/* Actions — administrator only */}
                {roleCode !== "nhai_officials" && (
                  <TableCell align="center">Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {!!loading ? (
                <TableRow>
                  <TableCell colSpan={roleCode === "nhai_officials" ? 6 : 7} align="center">
                    <Box sx={{ height: "450px" }}>
                      <DataLoader />{" "}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : displayedRows?.length > 0 ? (
                displayedRows?.map((interviewerInfo: any, index: any) => {
                  return (
                    <TableRow>
                      {/* Position Name — all roles */}
                      <TableCell align="left">
                        {interviewerInfo?.position_name || "NA"}
                      </TableCell>

                      {/* Candidate Name — all roles */}
                      <TableCell align="left">
                        {interviewerInfo?.candidate_name || "NA"}
                      </TableCell>

                      {/* Candidate Email — nhai_officials only */}
                      {roleCode === "nhai_officials" && (
                        <TableCell align="left">
                          {interviewerInfo?.candidate_email || "NA"}
                        </TableCell>
                      )}

                      {/* Project Code — nhai_officials only */}
                      {roleCode === "nhai_officials" && (
                        <TableCell align="center">
                          {interviewerInfo?.project_code ?? "-"}
                        </TableCell>
                      )}

                      {/* Status — all roles */}
                      <TableCell align="center">
                        {interviewerInfo?.application_display_status ==
                          "Interview Scheduled" ||
                          interviewerInfo?.application_display_status ==
                          "Applied" ||
                          interviewerInfo?.application_display_status ==
                          "Interview Rescheduled" ||
                          interviewerInfo?.application_display_status ==
                          "Empanelled" ||
                          interviewerInfo?.application_display_status ==
                          "Not-Empanelled" ||
                          interviewerInfo?.application_display_status ==
                          "Interview Re-scheduled" ||
                          interviewerInfo?.application_display_status ==
                          "Rescheduling in Progress" ||
                          interviewerInfo?.application_display_status ==
                          "Rescheduling Cancelled" ||
                          interviewerInfo?.application_display_status ==
                          "Rescheduling on Hold" ||
                          interviewerInfo?.application_display_status ==
                          "Interview On Hold" ||
                          interviewerInfo?.application_display_status ==
                          "Reschedule Failed" ||
                          interviewerInfo?.application_display_status ==
                          "Interview Completed" ? (
                          <SlotStatus
                            type={
                              interviewerInfo?.application_display_status ==
                                "Applied"
                                ? "applied"
                                : interviewerInfo?.application_display_status ==
                                  "Interview Scheduled"
                                  ? "interviewscheduled"
                                  : interviewerInfo?.application_display_status ==
                                    "Interview Rescheduled" ||
                                    interviewerInfo?.application_display_status ==
                                    "Interview Re-scheduled"
                                    ? "interviewrescheduled"
                                    : interviewerInfo?.application_display_status ==
                                      "Rescheduling in Progress"
                                      ? "reschedulinginprogress"
                                      : interviewerInfo?.application_display_status ==
                                        "Empanelled"
                                        ? "empaneled"
                                        : interviewerInfo?.application_display_status ==
                                          "Interview Completed"
                                          ? "interviewcompleted"
                                          : interviewerInfo?.application_display_status ==
                                            "Rescheduling Cancelled"
                                            ? "rescheduling_cancel"
                                            : interviewerInfo?.application_display_status ==
                                              "Rescheduling on Hold"
                                              ? "rescheduling_hold"
                                              : interviewerInfo?.application_display_status ==
                                                "Interview On Hold"
                                                ? "interview_hold"
                                                : interviewerInfo?.application_display_status ==
                                                  "Reschedule Failed"
                                                  ? "reschedule_failed"
                                                  : "notempaneled"
                            }
                          ></SlotStatus>
                        ) : (
                          <>
                            {interviewerInfo?.application_display_status ||
                              "NA"}
                          </>
                        )}
                      </TableCell>

                      {/* Description — administrator only */}
                      {roleCode !== "nhai_officials" && (
                        <TableCell align="left">
                          <Box sx={{ textAlign: "left" }}>
                            {interviewerInfo?.additional_notes || "NA"}
                          </Box>
                        </TableCell>
                      )}

                      {/* Remarks — administrator only */}
                      {roleCode !== "nhai_officials" && (
                        <TableCell align="left">
                          {interviewerInfo?.issue_notes ? (
                            <Box
                              sx={{
                                textAlign: "left",
                                color: "#c12323",
                                padding: "5px",
                                borderRadius: "3px",
                              }}
                            >
                              {interviewerInfo?.issue_notes}
                            </Box>
                          ) : (
                            <Box sx={{ textAlign: "left", padding: "5px" }}>
                              NA
                            </Box>
                          )}
                        </TableCell>
                      )}
                      {/* Candidate Source — administrator only */}
                      {roleCode !== "nhai_officials" && (
                        <TableCell align="left">
                          <Box
                            sx={{
                              textAlign: "center",
                            }}
                          >
                            {interviewerInfo?.candidate_source === 1 ? 'Bid' : interviewerInfo?.candidate_source === 2 ? 'Already Deployed' : interviewerInfo?.candidate_source === 3 ? 'Infracon' : '-'}
                          </Box>
                        </TableCell>
                      )}

                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        {(() => {
                          const completedApp = interviewerInfo?.scheduled_applications[0]?.slot_date;
                          // ?.find(
                          //   (app: any) => app.status === "completed"
                          // );
                          return completedApp
                            ? moment(completedApp).format("DD-MMM-YYYY")
                            : "NA";
                        })()}
                      </TableCell>


                      {/* Actions — administrator only */}
                      {roleCode !== "nhai_officials" && (
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: "flex",
                              gap: "2px",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <Tooltip title="View" arrow>
                              <IconButton
                                size="small"
                                sx={{ fontSize: "14px", color: "#4880FF" }}
                                className="table__icons"
                                onClick={() =>
                                  navigate(
                                    isReschuldingTracker
                                      ? `/admin/rescheduling-tracker/view/${interviewerInfo?.id}`
                                      : `/admin/interview-tracker/view/${interviewerInfo?.id}`,
                                    {
                                      state: {
                                        ...interviewerInfo,
                                        filterState: selectedScheduleStatus,
                                        paginationState: {
                                          page,
                                          rowsPerPage,
                                          offset,
                                        },
                                        searchState: searchValue,
                                        projectCodeSearchState: projectCodeSearchValue,
                                        dateRangeState: dateRange,
                                        candidateSourceState: candidateSource,
                                        positionState: selectedPosition,
                                      },
                                    }
                                  )
                                }
                              >
                                <img src={ViewIcons} className="view" />
                              </IconButton>
                            </Tooltip>
                            {(roleCode === "administrator" || roleCode === "talent_admin") && !isReschuldingTracker && (
                              <Tooltip title="View CV" arrow>
                                <IconButton
                                  size="small"
                                  className="table__icons"
                                  sx={{ fontSize: "14px", color: "#626262" }}
                                  onClick={() =>
                                    navigate("/admin/viewCV", { state: interviewerInfo })
                                  }
                                >
                                  <DescriptionOutlinedIcon className="view" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {!isReschuldingTracker && (
                              <Tooltip title="View Feedback" arrow>
                                <IconButton
                                  size="small"
                                  className="table__icons"
                                  sx={{
                                    fontSize: "14px",
                                    color: "#4880FF",
                                    opacity: !(
                                      interviewerInfo?.application_status ===
                                      "completed"
                                    )
                                      ? "50%"
                                      : "100%",
                                  }}
                                  disabled={
                                    !(
                                      interviewerInfo?.application_status ===
                                      "completed"
                                    )
                                  }
                                  onClick={() =>
                                    navigate(
                                      `/admin/interview-tracker/feed-back/${interviewerInfo?.id}`,
                                      {
                                        state: {
                                          ...interviewerInfo,
                                          filterState: selectedScheduleStatus,
                                          paginationState: {
                                            page,
                                            rowsPerPage,
                                            offset,
                                          },
                                          searchState: searchValue,
                                          dateRangeState: dateRange,
                                          candidateSourceState: candidateSource,
                                          positionState: selectedPosition,
                                        },
                                      }
                                    )
                                  }
                                >
                                  <img src={FeedBackIcon} className="feedback" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {!isReschuldingTracker && (
                              <Tooltip title="Eligibility Comparison" arrow>
                                <IconButton
                                  size="small"
                                  className="table__icons"
                                  sx={{ fontSize: "14px", color: "#4880FF" }}
                                  onClick={() =>
                                    navigate(
                                      `/admin/interview-tracker/eligibility-comparison/${interviewerInfo?.candidate_id?.id || interviewerInfo?.candidate_id}/${interviewerInfo?.candidate_eligibility_id}`,
                                      {
                                        state: {
                                          ...interviewerInfo,
                                          filterState: selectedScheduleStatus,
                                          paginationState: {
                                            page,
                                            rowsPerPage,
                                            offset,
                                          },
                                          searchState: searchValue,
                                          dateRangeState: dateRange,
                                          candidateSourceState: candidateSource,
                                          positionState: selectedPosition,
                                        },
                                      }
                                    )
                                  }
                                >
                                  <DifferenceIcon
                                    sx={{ color: "black", opacity: "60%" }}
                                  />
                                </IconButton>
                              </Tooltip>
                            )}
                            {!isReschuldingTracker &&
                              !["Empanelled", "Not-Empanelled", "Interview Completed"].includes(
                                interviewerInfo?.application_display_status
                              ) && (
                                <Tooltip title="TA Remarks" arrow>
                                  <IconButton
                                    size="small"
                                    className="table__icons"
                                    sx={{ fontSize: "14px", color: "#4880FF" }}
                                    onClick={() =>
                                      handleOpenTARemarks(interviewerInfo?.id)
                                    }
                                  >
                                    <img
                                      src={EditIcon}
                                      className="view"
                                      alt="Edit"
                                      width={25}
                                      height={21}
                                    />
                                  </IconButton>
                                </Tooltip>
                              )}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={roleCode === "nhai_officials" ? 6 : 7} align="center">
                    <Box
                      sx={{
                        height: "450px",
                      }}
                    >
                      <DataNotFound />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {totalRecords > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            component="div"
            count={totalRecords}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Box>
      <RemarksDialog
        open={openTARemarksDialog}
        loading={taLoading}
        title="TA Remarks"
        submitLabel="Save"
        hideFileUpload={true}
        initialValue={taRemark}
        onClose={() => setOpenTARemarksDialog(false)}
        onSubmit={(remark) => handleSaveTARemarks(remark)}
      />
    </>
  );
};
export default InterviewTrackerListView;
